import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import QRCode from 'qrcode'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'

import { direccionDelQr, useUnidadesParaEtiqueta, type UnidadParaEtiqueta } from './useUnidades'
import estilos from './PaginaEtiquetas.module.css'

/**
 * Las etiquetas de las herramientas, para imprimir y pegar.
 *
 * SE IMPRIME DEL NAVEGADOR y no se genera un PDF. Un PDF exigiría una
 * librería más, y lo que hay que hacer con esto es exactamente lo que un
 * navegador ya sabe: mandarlo a la impresora. La hoja está maquetada para
 * papel, no para pantalla — lo que se ve arriba son los mandos, y desaparecen
 * al imprimir.
 *
 * EL QR ES UNA DIRECCIÓN, y eso ahorra programar un lector. La cámara de
 * cualquier teléfono la reconoce, la ofrece de un toque y abre la aplicación
 * en la pantalla de devolver esa herramienta. No hay que enseñarle a nadie
 * dónde está el botón de escanear.
 *
 * SE GENERAN EN SVG y no en imagen: una etiqueta pegada en una escalera se
 * raya, se moja y se llena de polvo, así que el QR tiene que imprimirse con
 * el borde perfectamente nítido para que sobreviva algo de suciedad. Un PNG
 * escalado a seis centímetros llega borroso.
 */
export function PaginaEtiquetas() {
  const { unidades, cargando, error } = useUnidadesParaEtiqueta()
  const [soloEstas, setSoloEstas] = useState<Set<string>>(new Set())

  const aImprimir = useMemo(
    () => (soloEstas.size === 0 ? unidades : unidades.filter((u) => soloEstas.has(u.id))),
    [unidades, soloEstas],
  )

  function alternar(id: string) {
    setSoloEstas((previas) => {
      const siguiente = new Set(previas)
      if (siguiente.has(id)) siguiente.delete(id)
      else siguiente.add(id)
      return siguiente
    })
  }

  if (cargando) return <Cargando mensaje="IMPRIMIENDO ETIQUETAS CIRCUITOS..." />

  if (error) {
    return (
      <>
        <Volver />
        <Tarjeta>
          <Aviso tipo="error" titulo="No se pudieron cargar">
            {error}
          </Aviso>
        </Tarjeta>
      </>
    )
  }

  if (unidades.length === 0) {
    return (
      <>
        <Volver />
        <Tarjeta>
          <Aviso tipo="alerta" titulo="No hay etiquetas que imprimir">
            O no hay herramientas cargadas, o tu rol no puede ver los códigos. Los códigos solo
            los ve quien administra la bodega: con ellos en la mano se podría marcar como devuelta
            una herramienta que sigue en la calle.
          </Aviso>
        </Tarjeta>
      </>
    )
  }

  return (
    <>
      {/* ── Los mandos. No se imprimen. ─────────────────────────────────── */}
      <div className={estilos.mandos}>
        <Volver />

        <Tarjeta
          subtitulo="Se pegan en la herramienta. El técnico apunta la cámara y la aplicación abre sola."
          acciones={
            <div className={estilos.acciones}>
              {soloEstas.size > 0 && (
                <Boton variante="plano" onClick={() => setSoloEstas(new Set())}>
                  Quitar la selección
                </Boton>
              )}
              <Boton variante="principal" onClick={() => window.print()}>
                Imprimir {aImprimir.length === unidades.length ? 'las 25' : `${aImprimir.length}`}
              </Boton>
            </div>
          }
        >
          <p className={estilos.explicacion}>
            Sin marcar nada se imprimen todas. Marca solo las que necesites cuando haya que
            reponer una etiqueta rota — no gastes una hoja entera por una escalera.
          </p>

          <ul className={estilos.seleccion}>
            {unidades.map((u) => (
              <li key={u.id}>
                <label className={soloEstas.has(u.id) ? estilos.marcada : estilos.porMarcar}>
                  <input
                    type="checkbox"
                    checked={soloEstas.has(u.id)}
                    onChange={() => alternar(u.id)}
                  />
                  <span className="codigo">{u.etiqueta}</span>
                </label>
              </li>
            ))}
          </ul>
        </Tarjeta>
      </div>

      {/* ── La hoja. Esto es lo que sale por la impresora. ──────────────── */}
      <div className={estilos.hoja}>
        {aImprimir.map((u) => (
          <Etiqueta key={u.id} unidad={u} />
        ))}
      </div>
    </>
  )
}

/**
 * La salida.
 *
 * Esta pantalla se abre desde un botón del inventario y NO tiene entrada en
 * el menú, así que sin esto es un callejón sin salida: en el escritorio hay
 * que buscar la barra lateral, y en el teléfono la barra es un cajón cerrado
 * —no hay nada a la vista que devuelva a nadie a ninguna parte—.
 *
 * Va en los tres estados de la pantalla, incluidos el error y el vacío: son
 * justo donde alguien está más perdido y más ganas tiene de volver.
 */
function Volver() {
  return (
    <Link className={estilos.volver} to="/herramientas">
      <span aria-hidden="true">←</span> Inventario de herramientas
    </Link>
  )
}

/**
 * Una etiqueta.
 *
 * Tres cosas y en este orden de tamaño: el QR, que es lo que se usa; la
 * etiqueta en grande, que es lo que se lee cuando la cámara no enfoca o el
 * teléfono se quedó sin batería; y el nombre de la herramienta, pequeño,
 * para saber qué es sin escanear.
 */
function Etiqueta({ unidad }: { unidad: UnidadParaEtiqueta }) {
  const [svg, setSvg] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    void QRCode.toString(direccionDelQr(unidad.token), {
      type: 'svg',
      margin: 0,
      // Corrección alta: una etiqueta en una obra se raya y se ensucia, y con
      // este nivel el código sigue leyéndose con hasta un tercio del dibujo
      // estropeado. Cuesta un QR algo más denso y vale la pena.
      errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#FFFFFF' },
    }).then((s) => {
      if (vivo) setSvg(s)
    })
    return () => {
      vivo = false
    }
  }, [unidad.token])

  return (
    <article className={estilos.etiqueta}>
      <div
        className={estilos.qr}
        // Es un SVG generado por la librería a partir de nuestra propia
        // dirección: no viene de fuera ni lo escribe nadie.
        dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
      />
      <p className={estilos.codigoEtiqueta}>{unidad.etiqueta}</p>
      <p className={estilos.nombreEtiqueta}>{unidad.herramienta}</p>
      <p className={estilos.marca}>MEGA</p>
    </article>
  )
}
